<template>
<div v-if="showApprovalRounds" class="row">
  <div class="col-xs-4 text-bold q-px-sm">
    Definitieve toetsing
  </div>
  <div class="col" />
  <div class="col-xs-4 text-right text-bold q-px-sm">
    1e ronde toetsing
  </div>
</div>

<q-list v-if="ruleVersions.length" bordered separator class="rounded-borders">
  <q-item v-for="ruleVersion in ruleVersions" :key="ruleVersion.id" class="items-center">
    <q-item-section>
      <div class="row items-center">
        <div class="col-xs-2">
          <rule-approval-buttons
              v-if="$can('approveForProject', ruleVersion.rule)"
              class="col-xs-2"
              :rule-version="ruleVersion"
              direction="approver"
              @approve="onApprove(ruleVersion)"
              @rejectApproval="onReject(ruleVersion)"
              @makeRelevant="onMakeRelevant(ruleVersion)"
              @makeIrrelevant="onMakeNotRelevant(ruleVersion)"
              @restoreStatus="onRevert(ruleVersion)"
          />
          <div v-else-if="$can('checkOffRules', project) && !$ruleIsApproved(ruleVersion.pivot.approver_status)">
            <icon-button
                v-if="$ruleIsCheckedOff(ruleVersion.pivot.implementer_status)"
                icon="check_box"
                aria-label="Niet afgerond"
                size="sm"
                color="primary"
                round
                @click="onCheckOff(ruleVersion, 0)"
            />
            <icon-button
                v-else
                icon="check_box_outline_blank"
                :aria-label="$ruleIsIncomplete(ruleVersion.pivot.implementer_status) ?
                  'Afgerond' :
                  'Markeren is niet mogelijk als er dubbel getoetst wordt'"
                size="sm"
                color="primary"
                round
                @click="onCheckOff(ruleVersion, 1)"
                :disabled="!$ruleIsIncomplete(ruleVersion.pivot.implementer_status)"
            />
          </div>
        </div>
        <div class="col">
          <item-link
              dense
              :href="$route('project.rule.show', {project_id: project.id, phase_id: phaseId, rule_id: ruleVersion.rule_id})"
              linkClasses="link">
            <q-item-section avatar class="q-pr-lg">
              <template v-if="($can('manage-rules') || $can('updateBindingForProject', ruleVersion.rule_id))
                                && ruleVersion.rule.current_published_version?.id
                                && ruleVersion.rule.current_published_version?.id !== ruleVersion.id">
                <count-chip>
                  {{ ruleVersion.rule.code }} *
                  <q-tooltip>Deze richtlijn is niet gekoppeld aan de meest recente versie</q-tooltip>
                </count-chip>
              </template>
              <template v-else>
                <count-chip :count="ruleVersion.rule.code" />
              </template>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ ruleVersion.summary }}</q-item-label>
            </q-item-section>
          </item-link>
        </div>
        <div class="col-xs-2 text-right">
          <rule-approval-buttons
              v-if="$can('approveForProject', ruleVersion.rule)"
              :rule-version="ruleVersion"
              direction="implementer"
              @approve="onApprove(ruleVersion)"
              @rejectApproval="onReject(ruleVersion)"
              @makeRelevant="onMakeRelevant(ruleVersion)"
              @makeIrrelevant="onMakeNotRelevant(ruleVersion)"
              @restoreStatus="onRevert(ruleVersion)"
          />
          <rule-approval-buttons
              v-else
              :rule-version="ruleVersion"
              direction="approver"
          />
        </div>
      </div>
    </q-item-section>
  </q-item>
</q-list>
<rule-form-dialog
    v-model="showRuleFormDialog"
    :rule-version="currentRuleVersion"
    :project-id="project.id"
    :is-approve="isApprove"
/>
</template>

<script>
import { ruleMixin } from '~HIOR/mixins'
import RuleFormDialog from '~HIOR/pages/project/rule/dialog/RuleFormDialog'
import RuleApprovalButtons from '~HIOR/components/rule/RuleApprovalButtons'
import RuleStatus from '../../constants/ruleStatus'
import LoadingIndicator from '../element/LoadingIndicator'
import { useProjectRules } from '../../stores/useProjectRules'

export default {
  setup () {
    const projectRules = useProjectRules()
    return {
      projectRules,
    }
  },
  mixins: [ruleMixin],
  components: {
    LoadingIndicator,
    RuleFormDialog,
    RuleApprovalButtons,
  },
  props: {
    ruleVersions: {
      type: Object,
      default: () => {
        return {}
      },
    },
    category: {
      type: Object,
      default: () => {
        return {}
      },
    },
    project: {
      type: Object,
      required: true,
    },
    phaseId: {
      type: Number,
      required: true,
    },
  },
  data () {
    return {
      showRuleFormDialog: false,
      currentRuleVersion: null,
      isApprove: false,
    }
  },
  computed: {
    showApprovalRounds () {
      return this.ruleVersions.some(ruleVersion => {
        return this.$can('approveForProject', ruleVersion.rule) && (
            !this.$can('approve', ruleVersion.rule) || (
                !this.$ruleIsIncomplete(ruleVersion.pivot.implementer_status) &&
                !this.$ruleIsCheckedOff(ruleVersion.pivot.implementer_status)
            )
        )
      })
    },
  },
  methods: {
    ruleLabel (rule) {
      return `${rule.rule.code} - ${rule.summary}`
    },
    onApprove (rule) {
      this.currentRuleVersion = rule
      this.isApprove = true
      this.showRuleFormDialog = true
    },
    onReject (rule) {
      this.currentRuleVersion = rule
      this.isApprove = false
      this.showRuleFormDialog = true
    },
    async onCheckOff (ruleVersion, direction) {
      try {
        await axios.post(route('project.rule.checkoff', {
          project_id: this.project.id,
          rule_id: ruleVersion.id,
          direction,
        }))
        this.projectRules.reload()
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
    async onRevert (ruleVersion) {
      if (!await this.$confirm(
          {
            title: 'Richtlijn herstellen',
            messages: [`Weet u zeker dat u ${ruleVersion.rule.code} wilt herstellen? Hierbij vervalt het toetsingsresultaat.`],
          },
      )) {
        return
      }
      try {
        const response = await axios.post(route('project.rule.revert', {
          project_id: this.project.id,
          rule_id: ruleVersion.id,
        }))
        this.projectRules.reload()
        this.$toast('success', response.data.message)
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
    async onMakeRelevant (ruleVersion) {
      if (!await this.$confirm(
          {
            title: 'Richtlijn is wel relevant',
            messages: [`Weet u zeker dat u ${ruleVersion.rule.code} op van toepassing wilt zetten?`],
          },
      )) {
        return
      }
      try {
        const response = await axios.post(route('project.rule.hide', {
          project_id: this.project.id,
          rule_version_id: ruleVersion.id,
        }))
        this.projectRules.reload()
        this.$toast('success', response.data.message)
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
    async onMakeNotRelevant (ruleVersion) {
      if (!await this.$confirm(
          {
            title: 'Richtlijn is niet relevant',
            messages: [`Weet u zeker dat u ${ruleVersion.rule.code} op niet van toepassing wilt zetten?`],
          },
      )) {
        return
      }
      try {
        const response = await axios.post(route('project.rule.hide', {
          project_id: this.project.id,
          rule_version_id: ruleVersion.id,
        }))
        this.projectRules.reload()
        this.$toast('success', response.data.message)
      } catch (e) {
        console.error(e)
        this.$toast('error', 'De aanvraag kon niet worden uitgevoerd')
      }
    },
  },
}
</script>
